import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fileIcon = (ct) => {
  if (!ct) return Icons.File;
  if (ct.includes("pdf")) return Icons.FileText;
  if (ct.includes("image")) return Icons.FileImage;
  if (ct.includes("sheet") || ct.includes("csv")) return Icons.FileSpreadsheet;
  return Icons.File;
};
const fmtSize = (b) => b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB";

export default function Documents() {
  const { hasPerm } = useAuth();
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({ categories: [], link_types: [] });
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Other", link_type: "General", link_id: "", link_label: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (category !== "all") p.set("category", category);
    api.get(`/documents?${p.toString()}`).then(({ data }) => setDocs(data));
  }, [q, category]);
  useEffect(() => {
    api.get("/documents/meta").then(({ data }) => setMeta(data));
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title || file.name);
    fd.append("category", form.category);
    fd.append("link_type", form.link_type);
    fd.append("link_id", form.link_id);
    fd.append("link_label", form.link_label);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false); setFile(null); setForm({ title: "", category: "Other", link_type: "General", link_id: "", link_label: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("File not available"); }
  };
  const archive = async (d) => { try { await api.post(`/documents/${d.id}/archive`); toast.success("Archived"); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Document Center" subtitle="Secure central repository — attach files to employees, assets and records"
        actions={hasPerm("document.upload") && <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-doc-button"><Icons.Upload className="h-4 w-4 mr-1.5" /> Upload</Button>} />

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-9" data-testid="doc-search" /></div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Document</TableHead><TableHead className="hidden md:table-cell">Category</TableHead><TableHead className="hidden lg:table-cell">Linked To</TableHead><TableHead className="hidden lg:table-cell">Ver</TableHead><TableHead className="hidden md:table-cell">Uploaded</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="documents-table">
            {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No documents.</TableCell></TableRow>}
            {docs.map((d) => {
              const FI = fileIcon(d.content_type);
              return (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell><div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><FI className="h-5 w-5" /></div>
                    <div><div className="text-sm font-medium text-slate-800">{d.title}</div><div className="text-xs text-slate-500">{d.original_filename} · {fmtSize(d.size)}</div></div>
                  </div></TableCell>
                  <TableCell className="hidden md:table-cell"><Badge variant="secondary" className="text-[10px]">{d.category}</Badge></TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">{d.link_type}{d.link_label ? ` · ${d.link_label}` : ""}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs font-mono text-slate-500">v{d.version}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-slate-400">{fmtDateTime(d.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}><Icons.Download className="h-4 w-4" /></Button>
                      {hasPerm("document.archive") && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => archive(d)} data-testid={`doc-archive-${d.id}`}><Icons.Archive className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>File</Label>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg p-6 cursor-pointer hover:border-blue-300" data-testid="doc-file-dropzone">
                <Icons.UploadCloud className="h-7 w-7 text-slate-400 mb-2" />
                <span className="text-sm text-slate-600">{file ? file.name : "Click to choose a file (max 25MB)"}</span>
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" />
              </label>
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={file?.name || "Document title"} data-testid="doc-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger><SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Link Type</Label>
                <Select value={form.link_type} onValueChange={(v) => setForm({ ...form, link_type: v, link_id: "", link_label: "" })}><SelectTrigger data-testid="doc-link-type"><SelectValue /></SelectTrigger><SelectContent>{meta.link_types.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {form.link_type === "Employee" && (
              <div className="space-y-2"><Label>Employee</Label>
                <Select value={form.link_id} onValueChange={(v) => { const e = employees.find((x) => x.id === v); setForm({ ...form, link_id: v, link_label: e ? `${e.first_name} ${e.last_name}` : "" }); }}>
                  <SelectTrigger data-testid="doc-link-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_no})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={upload} disabled={!file || uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="doc-upload-submit">{uploading ? "Uploading…" : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
